import * as React from "react";
import { FC, JSX, useMemo } from "react";
import ProjectRunConfiguration from "@app/projects/projectRunConfiguration";
import { ProviderNetworkTypeEnum } from "@app/shared/enums/providerNetworkTypeEnum";
import ProjectOptions from "@app/projects/projectOptions";
import AccessSummary from "@app/projects/results/adequacy/accessSummary";
import { buildAdequacyProvidersColumnConfig } from "@app/projects/servicing-provider/ServicingProviderHelpers";
import { NetNewServicingProviderTableData } from "@app/projects/servicing-provider/types";
import { SortDirections, WafflesPaginationProps } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import { TableType, ViewableField } from "@app/viewable-fields/viewableFields";

type DisplayedProviderType = "targeted" | "nonTargeted";

interface NetNewServicingProviderTableProps {
  accessSummary: AccessSummary;
  bulkActionBar: JSX.Element;
  data: NetNewServicingProviderTableData[];
  displayedProviderType: DisplayedProviderType;
  providerType: ProviderNetworkTypeEnum;
  handleSort: (col: keyof NetNewServicingProviderTableData, direction: SortDirections) => void;
  loading: boolean;
  project: ProjectOptions;
  runConfiguration: ProjectRunConfiguration;
  onSelectedProvidersChanged: React.Dispatch<React.SetStateAction<string[]>>
  selectedProviders: string[];
  setSkip: (newSkip: number) => void;
  skip: number;
  tableType: TableType;
  take: number;
  totalRecords: number;
  viewableFields: ViewableField[];
  showProviderValidations?: boolean;
  showSpecialtyPredictionModel?: boolean;
  showPgrConfidenceTier?: boolean;
}

export const NetNewServicingProviderTable: FC<React.PropsWithChildren<NetNewServicingProviderTableProps>> = (props: NetNewServicingProviderTableProps) => {
  const columnConfig = useMemo(() => {
    return buildAdequacyProvidersColumnConfig(
      props.viewableFields,
      props.tableType,
      props.project.showNppesDates && props.runConfiguration && props.runConfiguration.showNppesDates,
      props.project.showMipsScore && props.runConfiguration && props.runConfiguration.showMipsScore,
      props.project.showPciData && props.runConfiguration && props.runConfiguration.showPciData,
      props.project.showVaClaimsVolumeColumn,
      props.project.showTricareClaimsVolumeColumn,
      false, //avg dist
      false, // avg time
      true, //projectionReason
      true, //isNetNewConfig
      false, //isNextClosestImpactConfig
      false, //showExclusionSource
      props.project.showLocationConfidenceScores && props.runConfiguration?.showLocationConfidenceScores,
      props.project.showMotiveAppropriatenessScore && props.runConfiguration?.showMotiveAppropriatenessScore,
      props.runConfiguration?.showCareJourneyScores,
      undefined, //showProviderAffiliations
      undefined, //affiliationsDisplayOnNetworkAccordion
      undefined, //showProviderGrouping
      props.showProviderValidations, //showProviderValidations
      undefined, //isZipProject
      props.showSpecialtyPredictionModel,
      props.showPgrConfidenceTier,
    );

  }, [props.viewableFields, props.tableType, props.project, props.runConfiguration, props.displayedProviderType, props.showPgrConfidenceTier]);

  const paginationProps: WafflesPaginationProps = {
    changePage: (newPageNumber) => { props.setSkip((newPageNumber - 1) * props.take) },
    recordsPerPage: props.take,
    skip: props.skip,
    totalRecords: props.totalRecords,
  };

  const getRowColor = (row: NetNewServicingProviderTableData) => {
    let className = "danger";
    const gainsPercentage = parseFloat(row.gainsPercent);
    const servesPercentage = parseFloat(row.servesPercent);
    const impactsPercentage = parseFloat(row.impactsPercent);
    const passPercentage = props.accessSummary.passPercentage;

    if (gainsPercentage > 0 || impactsPercentage < 100) {
      if (impactsPercentage >= passPercentage) {
        className = "success";
      }
      else if (impactsPercentage >= 25.0) {
        className = "warning";
      }
    }
    else {
      if (servesPercentage >= passPercentage) {
        className = "success";
      }
      else if (servesPercentage >= 25.0) {
        className = "warning";
      }
    }
    return className;
  }

  return (
    <Waffles<NetNewServicingProviderTableData>
      bulkActionBar={props.bulkActionBar}
      columns={columnConfig}
      customSort={(segment, col, direction) => { props.handleSort(col, direction); return segment; }}
      data={props.data}
      enablePagination={true}
      fullWidth={true}
      handleCheckboxChange={props.onSelectedProvidersChanged}
      loading={props.loading}
      paginationProps={paginationProps}
      passThroughSorting={true}
      rowDecorator={getRowColor}
      selectedKeys={props.selectedProviders}
      showCheckboxes={true}
      testId={`${props.providerType}-provider-table`}
    />
  );
}